import React, { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { FiAlertTriangle } from 'react-icons/fi';
import { getGoogleMapsConfig } from '../../utils/mapsConfig';

// Map container style
const containerStyle = {
  width: '100%',
  height: '100%'
};

// Default center (New York City)
const defaultCenter = {
  lat: 40.7128,
  lng: -74.006
};

// Map options
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  mapTypeId: 'roadmap'
};

const AlertDetailMap = ({ position, vehicle, geofence, type }) => {
  const [map, setMap] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(true);
  
  // Load Google Maps script with shared config
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsConfig());
  
  // Handle map load
  const onLoad = useCallback((map) => {
    setMap(map);
    
    // Center map on alert position
    if (position) {
      map.setCenter(position);
      map.setZoom(15);
    }
  }, [position]);
  
  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  
  // Get marker icon based on alert type
  const getMarkerIcon = () => {
    // For geofence events, use the vehicle status to determine the icon
    if (type === 'geofence_entry' || type === 'geofence_exit') {
      let iconColor = 'gray';
      
      if (vehicle) {
        switch (vehicle.status) {
          case 'active':
            iconColor = 'green';
            break;
          case 'idle':
            iconColor = 'yellow';
            break;
          case 'stopped':
            iconColor = 'red';
            break;
          default:
            iconColor = 'gray';
        }
      }
      
      return {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 5,
        fillColor: iconColor,
        fillOpacity: 1,
        strokeWeight: 1,
        rotation: 0 // Could use vehicle.heading here if available
      };
    }
    
    // For other alert types
    switch (type) {
      case 'speeding':
        return {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#EC4E20', // Orange
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#9A3012'
        };
      case 'idle_time':
        return {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#FFC107', // Yellow
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#B7950B'
        };
      case 'offline':
        return {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#DC3545', // Red
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#9A2530'
        };
      default:
        return {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#6C757D', // Gray
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#495057'
        };
    }
  };
  
  // Circle geofence options
  const circleOptions = {
    fillColor: '#1A5F7A',
    fillOpacity: 0.2,
    strokeColor: '#1A5F7A',
    strokeOpacity: 0.8,
    strokeWeight: 2
  };
  
  // Render loading message
  if (loadError) {
    return <div className="text-ui-error p-4">Error loading maps</div>;
  }
  
  if (!isLoaded) {
    return <div className="flex justify-center items-center h-full">Loading maps...</div>;
  }
  
  // For simplicity, we'll just use a circle for geofence visualization
  // In a real app, you would fetch the actual geofence details
  const geofenceRadius = 500; // Default radius in meters
  
  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={position || defaultCenter}
      zoom={15}
      options={mapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {/* Alert position marker */}
      {position && (
        <Marker
          position={position}
          icon={getMarkerIcon()}
          onClick={() => setInfoWindowOpen(true)}
        />
      )}
      
      {/* Info window */}
      {position && infoWindowOpen && (
        <InfoWindow
          position={position}
          onCloseClick={() => setInfoWindowOpen(false)}
        >
          <div className="p-1 max-w-xs">
            <div className="flex items-center text-sm mb-1">
              <FiAlertTriangle className="text-ui-warning mr-1" />
              <span className="font-medium">Alert Location</span>
            </div>
            {vehicle && (
              <p className="text-sm">
                {vehicle.name} ({vehicle.registration})
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
            </p>
          </div>
        </InfoWindow>
      )}
      
      {/* Geofence circle (for geofence alerts) */}
      {(type === 'geofence_entry' || type === 'geofence_exit') && geofence && (
        <Circle
          center={position} // In a real app, use the actual geofence center
          radius={geofenceRadius} // In a real app, use the actual radius
          options={circleOptions}
        />
      )}
    </GoogleMap>
  );
};

export default AlertDetailMap;